/**
 * Life-Stage Lanes (BuildPilot 2026 expansion) - v2
 *
 * Warmer, more human evolution of the original data map. Key changes
 * from v1:
 *   - Age band moved from a stark "18 - 25" chip in the corner into
 *     a soft "Around your late-teens and twenties" copy line woven
 *     into the emotional intro. Feels less clinical.
 *   - Added `image` field so each lane can carry a real BuildPilot
 *     hero photo. No AI-generated art, no pastel gradients - actual
 *     Adelaide homes, land shots, decks and interiors from our own
 *     photo library.
 *   - Reworded eyebrows to feel like a friend describing where you
 *     are, not a UX designer labelling a persona.
 *
 * Slugs are stable - do NOT rename without updating:
 *   - /app/frontend/src/App.js route
 *   - inbound links from LifeStageLanesSection
 *   - any future SEO / sitemap wiring
 */

export const LIFE_STAGE_LANES = [
  {
    slug: 'just-curious',
    eyebrow: 'Just poking around',
    ageWhisper: 'Usually late-teens through twenties',
    title: 'I want to understand how this whole home thing works',
    emotion:
      'You are not ready to buy, build or borrow. You are just trying to figure out if any of this is even possible for someone like you.',
    ctaLabel: 'Show me the basics',
    image: '/images/heroes/home%20design.webp',
    imageAlt: 'A quiet morning inside a new Adelaide home with soft light through a window',
    hub: {
      heading: 'Start with the basics, no pressure',
      intro:
        'We will walk you through the language, the money and the moves that first-home buyers wish someone had explained earlier. Nothing here signs you up to anything.',
      sections: [
        {
          heading: 'Get the language down first',
          links: [
            { label: 'A-Z of building terminology', to: '/terminology' },
            { label: 'What is a build contract, in plain English', to: '/building-contracts-adelaide-guide' },
            { label: 'How the NCC affects your future home', to: '/terminology/ncc' },
          ],
        },
        {
          heading: 'Understand the money',
          links: [
            { label: 'First Home Owner Grant SA', to: '/first-home-owner-grant-sa-adelaide' },
            { label: 'First Home Guarantee scheme', to: '/first-home-guarantee-scheme-adelaide' },
            { label: 'First Home Super Saver Scheme', to: '/first-home-super-saver-adelaide' },
          ],
        },
        {
          heading: 'Learn while you get on with life',
          links: [
            { label: 'Home Building Hero Podcast', to: '/hbh-episodes' },
            { label: 'Editorial guides index', to: '/guides' },
          ],
        },
      ],
    },
  },
  {
    slug: 'saving-up',
    eyebrow: 'Putting money away',
    ageWhisper: 'Usually twenties through early-thirties',
    title: 'I am saving but the questions keep piling up',
    emotion:
      'You know you want to own something one day. The number is intimidating, the timeline is fuzzy, and everyone gives you conflicting advice.',
    ctaLabel: 'Show me the money moves',
    image: '/images/heroes/choosing%20land.webp',
    imageAlt: 'A young couple walking across an empty Adelaide block, thinking about their first home',
    hub: {
      heading: 'The money side, without a broker in your ear',
      intro:
        'These are the pieces first-home buyers said made the biggest difference to their deposit and their borrowing power. All BuildPilot editorial - no product sales.',
      sections: [
        {
          heading: 'Government schemes that actually help',
          links: [
            { label: 'First Home Owner Grant SA', to: '/first-home-owner-grant-sa-adelaide' },
            { label: 'First Home Guarantee', to: '/first-home-guarantee-scheme-adelaide' },
            { label: 'Family Home Guarantee', to: '/family-home-guarantee-adelaide' },
            { label: 'First Home Super Saver Scheme', to: '/first-home-super-saver-adelaide' },
          ],
        },
        {
          heading: 'Should you build, buy or wait?',
          links: [
            { label: 'Build vs buy in Adelaide - factors, not figures', to: '/build-vs-buy-adelaide-guide' },
            { label: 'How long a build actually takes', to: '/building-timeline-adelaide-guide' },
            { label: 'Adelaide market report (live approvals data)', to: '/adelaide-building-market-report' },
          ],
        },
        {
          heading: 'Have a squiz at your future partners',
          links: [
            { label: 'Adelaide builders index', to: '/home-builders-adelaide' },
            { label: 'Browse Adelaide home designs', to: '/home-designs' },
          ],
        },
      ],
    },
  },
  {
    slug: 'ready-to-build',
    eyebrow: 'Ready to build',
    ageWhisper: 'Usually late-twenties through forties',
    title: 'I am ready to talk to builders',
    emotion:
      'You have your finance, your land or your plan. You just need three builders who can actually do what you want, in your budget, on your block.',
    ctaLabel: 'Match me with 3 builders',
    ctaOpensMatcher: true,
    image: '/images/heroes/new%20homes.webp',
    imageAlt: 'A crisp new-build Adelaide home ready for its first family',
    hub: {
      heading: 'You are at the pointy end. Let us line up 3 builders.',
      intro:
        'The matcher takes about 4 minutes. You will end up with 3 shortlisted builders who actually build the type of home you want, in the region you want, in your budget band. They will contact you within 48 hours.',
      sections: [
        {
          heading: 'Start the matcher',
          primaryCta: {
            label: 'Match me with 3 builders in 4 minutes',
            opensMatcher: 'professional:builder',
          },
          links: [
            { label: 'What to ask before you sign anything', to: '/building-contracts-adelaide-guide' },
            { label: 'How the timeline usually unfolds', to: '/building-timeline-adelaide-guide' },
          ],
        },
        {
          heading: 'While you wait for their calls',
          links: [
            { label: 'Browse home designs by our partner builders', to: '/home-designs' },
            { label: 'See house-and-land packages', to: '/adelaide-house-and-land' },
            { label: 'Understand fixed price vs cost plus contracts', to: '/fixed-price-vs-cost-plus-adelaide' },
          ],
        },
      ],
    },
  },
  {
    slug: 'building-now',
    eyebrow: 'In the middle of it',
    ageWhisper: 'Any age - this is a season, not an age',
    title: 'I am mid-build and I need help',
    emotion:
      'You signed. Now variations are stacking up, decisions are piling on, and something feels off with the builder. It is louder in your head than anyone warned you.',
    ctaLabel: 'Show me the survival kit',
    image: '/images/heroes/changing%20builders.webp',
    imageAlt: 'A partially framed Adelaide home in mid-construction, sun on the timber',
    hub: {
      heading: 'The mid-build survival kit',
      intro:
        'This is the phase nobody warns you about. Variations, stage inspections, colour selection, missed deadlines. These are the guides and tools our team wishes we had in our own builds.',
      sections: [
        {
          heading: 'Contracts, variations and the money side',
          links: [
            { label: 'Building contracts glossary', to: '/building-contracts-adelaide-guide' },
            { label: 'Fixed price vs cost plus', to: '/fixed-price-vs-cost-plus-adelaide' },
            { label: 'Provisional sums explained', to: '/terminology/provisional-sums' },
            { label: 'Prime cost items explained', to: '/terminology/prime-costs' },
          ],
        },
        {
          heading: 'Stages, inspections and the paperwork',
          links: [
            { label: 'How long does a build actually take?', to: '/building-timeline-adelaide-guide' },
            { label: 'Fixing out stage explained', to: '/terminology/fixing-out' },
            { label: 'Practical completion & handover', to: '/terminology/practical-completion' },
          ],
        },
        {
          heading: 'When it is not going well',
          links: [
            { label: 'Dispute with your builder - your options', to: '/dispute-with-builder-adelaide' },
            { label: 'One-on-one support', to: '/one-on-one-support' },
            { label: 'Complaints process explainer', to: '/complaints-process' },
          ],
        },
      ],
    },
  },
  {
    slug: 'just-moved-in',
    eyebrow: 'Fresh set of keys',
    ageWhisper: 'Any age - this is a moment, not an age',
    title: 'I just moved in and now what?',
    emotion:
      'The house is yours. There is a defect list you have not started, an appliance drawer full of manuals, and a partner asking who to ring about the front door.',
    ctaLabel: 'Help me settle in',
    image: '/images/heroes/decking.webp',
    imageAlt: 'A newly finished Adelaide home with a fresh timber deck catching afternoon light',
    hub: {
      heading: 'Your first 12 months. Do not lose the receipts.',
      intro:
        'This is where most build platforms disappear. BuildPilot stays with you through the defect window, the warranty period, and every squeaky floorboard, because your build is not really finished until year one is over.',
      sections: [
        {
          heading: 'Defects, warranty and the 90-day window',
          links: [
            { label: 'Warranty and after-handover explained', to: '/warranty-and-after-handover-adelaide' },
            { label: 'Practical completion glossary', to: '/terminology/practical-completion' },
            { label: 'Defects list glossary', to: '/terminology/defects' },
            { label: 'Snag list explained', to: '/terminology/snag-list' },
          ],
        },
        {
          heading: 'Home Log - the appliance, warranty & maintenance tracker',
          links: [
            { label: 'Open your Home Log', to: '/workspace/home-log' },
          ],
        },
        {
          heading: 'Landscaping, fences and finishing touches',
          links: [
            { label: 'Landscaping and outdoor guides', to: '/guides' },
            { label: 'Home designs by finish style', to: '/home-designs' },
          ],
        },
      ],
    },
  },
  {
    slug: 'been-here-a-while',
    eyebrow: 'Living in it for a bit',
    ageWhisper: 'Usually thirties through sixties',
    title: 'Life changed. My house needs to catch up.',
    emotion:
      'A second child, a home office, a partner working from home, or a nest that is emptying. The house you built is not quite the house you need now.',
    ctaLabel: 'What are my options?',
    image: '/images/heroes/renovations.webp',
    imageAlt: 'An older Adelaide home mid-renovation - existing brick with new joinery and glazing',
    hub: {
      heading: 'Extend, renovate, downsize or move on',
      intro:
        'These are the big decisions we help people work through in years 3-10 of ownership. All neutral. No agenda to sell you anything.',
      sections: [
        {
          heading: 'Extend and renovate',
          links: [
            { label: 'Home extensions in Adelaide', to: '/home-extensions-adelaide' },
            { label: 'Character home renovation', to: '/character-home-renovation-adelaide' },
            { label: 'Energy efficient home upgrades', to: '/energy-efficient-home-guide' },
          ],
        },
        {
          heading: 'Downsize or reimagine',
          links: [
            { label: 'Downsizer home builders Adelaide', to: '/downsizer-home-builders-adelaide' },
            { label: 'Narrow block builders', to: '/narrow-block-builder-adelaide' },
            { label: 'Duplex builders Adelaide', to: '/duplex-builders-adelaide' },
          ],
        },
        {
          heading: 'When you are ready to talk to a builder',
          primaryCta: {
            label: 'Match me with 3 builders',
            opensMatcher: 'professional:builder',
          },
          links: [
            { label: 'Adelaide builders index', to: '/home-builders-adelaide' },
          ],
        },
      ],
    },
  },
];

export const LIFE_STAGE_BY_SLUG = LIFE_STAGE_LANES.reduce((acc, lane) => {
  acc[lane.slug] = lane;
  return acc;
}, {});
